﻿import { useState } from "react";
import { Save, X, Pencil, Trash2, Check, MessageSquarePlus } from "lucide-react";
import type { MemoryStructure, QualityFeedback } from "../../types/orgMemory";

type GlobalMemoryHeaderProps = {
  categories: string[];
  globalTags: string[];
  feedback: QualityFeedback[];
  isSaving: boolean;
  onUpdateGlobal: (updates: Partial<Pick<MemoryStructure, "categories" | "globalTags">>) => void;
  onUpdateFeedback: (feedback: QualityFeedback[]) => void;
  onSave: () => void;
};

type TagListProps = {
  label: string;
  items: string[];
  onAdd: (val: string) => void;
  onRemove: (val: string) => void;
  placeholder: string;
};

function TagList({ label, items, onAdd, onRemove, placeholder }: TagListProps) {
  const [input, setInput] = useState("");

  const add = () => {
    const val = input.trim();
    if (!val || items.includes(val)) { setInput(""); return; }
    onAdd(val);
    setInput("");
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</label>
      <div className="flex flex-wrap gap-1 mb-2 min-h-[28px]">
        {items.map((item) => (
          <span key={item} className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
            {item}
            <button onClick={() => onRemove(item)} className="hover:text-blue-900"><X className="size-2.5" /></button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-gray-400 italic">None</span>}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="flex-1 rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
          placeholder={placeholder}
        />
        <button onClick={add} className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700">Add</button>
      </div>
    </div>
  );
}

type FeedbackCardProps = {
  feedback: QualityFeedback;
  onSave: (text: string) => void;
  onDelete: () => void;
};

function FeedbackCard({ feedback, onSave, onDelete }: FeedbackCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(feedback.text);

  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    onSave(v);
    setEditing(false);
  };

  const cancel = () => { setDraft(feedback.text); setEditing(false); };

  if (editing) {
    return (
      <div className="rounded-lg border border-emerald-300 bg-white p-3 space-y-2">
        <textarea
          autoFocus
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
        <div className="flex gap-2">
          <button onClick={commit} className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700">
            <Check className="size-3.5" /> Save
          </button>
          <button onClick={cancel} className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
            <X className="size-3.5" /> Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow">
      <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => setEditing(true)} title="Edit" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
          <Pencil className="size-3.5" />
        </button>
        <button onClick={onDelete} title="Delete" className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
          <Trash2 className="size-3.5" />
        </button>
      </div>
      <p className="pr-12 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{feedback.text}</p>
      <p className="mt-2 text-xs text-gray-400">{feedback.timestamp}</p>
    </div>
  );
}

export const GlobalMemoryHeader = ({
  categories,
  globalTags,
  feedback,
  isSaving,
  onUpdateGlobal,
  onUpdateFeedback,
  onSave,
}: GlobalMemoryHeaderProps) => {
  const [newFeedback, setNewFeedback] = useState("");

  const addFeedback = () => {
    const v = newFeedback.trim();
    if (!v) return;
    const entry: QualityFeedback = {
      id: `fb-${Date.now()}`,
      text: v,
      timestamp: new Date().toISOString(),
    };
    onUpdateFeedback([entry, ...feedback]);
    setNewFeedback("");
  };

  const editFeedback = (id: string, text: string) =>
    onUpdateFeedback(feedback.map((f) => (f.id === id ? { ...f, text } : f)));

  const removeFeedback = (id: string) =>
    onUpdateFeedback(feedback.filter((f) => f.id !== id));

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-4">
      <p className="text-xs font-bold uppercase tracking-widest text-blue-500">Global Memory Settings</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TagList
          label="Categories"
          items={categories}
          onAdd={(val) => onUpdateGlobal({ categories: [...categories, val] })}
          onRemove={(val) => onUpdateGlobal({ categories: categories.filter((c) => c !== val) })}
          placeholder="Add category"
        />
        <TagList
          label="Global Tags"
          items={globalTags}
          onAdd={(val) => onUpdateGlobal({ globalTags: [...globalTags, val] })}
          onRemove={(val) => onUpdateGlobal({ globalTags: globalTags.filter((t) => t !== val) })}
          placeholder="Add tag"
        />
      </div>

      {/* Org-wide quality feedback — kept separate from categories/tags above */}
      <div className="rounded-lg border border-blue-200 bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <MessageSquarePlus className="size-4 text-blue-500 shrink-0" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-500">Quality Feedback</p>
            <p className="text-xs text-gray-500">
              Improvement notes from policy feedback for this organisation. These are applied to future policy generation here.
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <textarea
            rows={2}
            value={newFeedback}
            onChange={(e) => setNewFeedback(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addFeedback(); } }}
            placeholder="Add an improvement note and press Add…"
            className="flex-1 rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />
          <button
            onClick={addFeedback}
            disabled={!newFeedback.trim()}
            className="self-stretch rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Add
          </button>
        </div>

        {feedback.length === 0 ? (
          <p className="py-4 text-center text-xs text-gray-400 italic">
            No quality feedback yet. Notes added through the policy feedback dialog also appear here.
          </p>
        ) : (
          <div className="space-y-2">
            {feedback.map((f) => (
              <FeedbackCard
                key={f.id}
                feedback={f}
                onSave={(text) => editFeedback(f.id, text)}
                onDelete={() => removeFeedback(f.id)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={isSaving}
          className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          <Save className="size-4" />
          {isSaving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
};
